<template>
  <component :is="tag" v-on="$listeners">
    <div class="operator-group">
      <b-avatar
        v-if="channel.operatorAssigned"
        class="merchant-profile"
        size="42"
        :variant="isActive ? 'light-light' : 'light-primary'"
        :src="channel.merchantUserProfile"
        :text="avatarText(channel.merchantUserName)"
      >
        <feather-icon
          v-if="!channel.merchantUserProfile && !channel.merchantUserName"
          icon="UserIcon"
          size="22"
        />
      </b-avatar>
      <b-avatar
        size="42"
        :badge="true"
        class="badge-minimal customer-profile"
        :badge-variant="channel.isOnline ? 'success' : 'secondary'"
        :variant="isActive ? 'light-light' : 'light-primary'"
      >
        <feather-icon icon="UserIcon" size="22" />
      </b-avatar>
    </div>
    <div class="chat-info flex-grow-1">
      <h5 class="mb-0 text-truncate">
        {{ getChatName(channel) }}
      </h5>
      <p v-if="channel.latestMessage" class="card-text text-truncate">
        <span v-if="channel.latestMessage.messageType == messageType.text">
          <span v-if="channel.latestMessage.isBot == true">
            <span v-if="channel.latestMessage.botType == botType.ADVANCED">
              {{ $t('chat.aiAssistant') }}
            </span>
            <span v-else>
              {{ $t('chat.chatBot') }}
            </span>
          </span>
          <span v-else>
            {{
              channel.latestMessage.participantId == userData.id &&
              channel.latestMessage.participantType == 'merchant'
                ? $t('chat.you')
                : getLastMessageName(channel.latestMessage.nameTranslate || channel.latestMessage.name)
            }}
          </span>
          : {{ channel.latestMessage.message }}
        </span>
        <span v-if="channel.latestMessage.messageType == messageType.system">
          {{
            channel.latestMessage.participantType == "merchant"
              ? $t("chat.you")
              : channel.latestMessage.name
          }}
          :
          {{
            channel.latestMessage.chatSettings[$i18n.locale.split("-")[0]]
              ? channel.latestMessage.chatSettings[$i18n.locale.split("-")[0]]
              : channel.latestMessage.chatSettings["en"]
                ? channel.latestMessage.chatSettings["en"]
                : channel.latestMessage.message
          }}
        </span>
        <span
          v-if="channel.latestMessage.messageType == messageType.attachment"
        >
          {{
            $t("chat.sendAnAttachment", {
              user:
                channel.latestMessage.participantId == userData.id &&
                channel.latestMessage.participantType == "merchant"
                  ? $t("chat.you")
                  : channel.latestMessage.name,
            })
          }}
        </span>
        <span v-if="channel.latestMessage.messageType == messageType.status">
          <span v-if="channel.latestMessage.message == 'action.rate'">
            <span v-if="channel.rateData.comment">
              {{
                $t(channel.latestMessage.message, {
                  visitor: channel.name,
                  rate: channel.rateData.rate,
                  comment: channel.rateData.comment,
                })
              }}
            </span>
            <span v-else>
              {{
                $t("action.rateNoComment", {
                  visitor: channel.name,
                  rate: channel.rateData.rate,
                })
              }}
            </span>
          </span>
          <span v-else>
            {{
              $t(channel.latestMessage.message, {
                operator: channel.latestMessage.name,
              })
            }}
          </span>
        </span>
        <span v-if="channel.latestMessage.messageType == messageType.joint">
          <span v-if="channel.latestMessage.isBot">
            <span v-if="channel.latestMessage.botType == botType.ADVANCED">
              {{ $t("chat.aiAssistantJoined") }}
            </span>
            <span v-else>
              {{ $t("chat.chatBotJoined") }}
            </span>
          </span>
          <span v-else>
            {{
              channel.latestMessage.participantId == userData.id &&
              channel.latestMessage.participantType == "merchant"
                ? `${$t("chat.you")} ${$t("chat.haveJoined")}`
                : `${channel.latestMessage.name} ${$t("chat.joined")}`
            }}
          </span>
        </span>
        <span
          v-if="channel.latestMessage.messageType == messageType.autoTranslate"
        >
          {{ `${$t(channel.latestMessage.message)}` }}
        </span>
        <span v-if="channel.transferredFrom && !channel.latestMessage.message">
          {{
            $t("general.transferFromOtherMerchant", {
              fromMerchant: channel.transferredFromName,
            })
          }}
        </span>
      </p>
    </div>
    <div class="chat-meta text-nowrap">
      <small v-if="channel.latestMessage" class="d-block mb-25 chat-time">
        {{ channel.latestMessage.createdAt | formatShortDate }}
      </small>
      <div class="d-flex align-items-center justify-content-end">
        <b-badge
          v-if="channel.badgeCount"
          pill
          variant="primary"
          class="mr-1"
        >
          {{ channel.badgeCount }}
        </b-badge>
        <div
          v-if="channel.serviceSystem && channel.serviceSystem.status != 'resolved' && channel.serviceSystem.status != 'cancel'"
          v-b-tooltip.hover.top
          :title="$t('chat.serviceActivating')"
          class="service-indicator"
        >
          <feather-icon
            icon="CastIcon"
            size="14"
            class="pulsing-icon"
          />
        </div>
      </div>
    </div>
  </component>
</template>

<script>
import { BAvatar, BBadge, BTooltip } from "bootstrap-vue";
import messageType from "@/data/messageType";
import botType from '@/data/botType'
import useJwt from "@/auth/jwt/useJwt";

export default {
  components: {
    BAvatar,
    BBadge,
    BTooltip,
  },
  props: {
    tag: {
      type: String,
      default: "div",
    },
    channel: {
      type: Object,
      required: true,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      messageType,
      botType,
    };
  },
  computed: {
    userData() {
      return this.$store.state.profile.email || this.$store.state.profile.phone  
        ? this.$store.state.profile
        : JSON.parse(
            localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName)
          );
    },
    locale() {
      return this.$i18n.locale.split('-')[0]
    },
  },
  methods: {
    getChatName(channel) {
      const name = channel.nameTranslate || channel.name 
      if (typeof name === 'object') {
        return name[this.locale] || name.en || name.zh || name.km || name.vi || name
      }
      return name
    },
    getLastMessageName(name) {      
      if (typeof name === 'object' && !Array.isArray(name)) {
        return name?.[this.locale] || name?.en || name?.zh || name?.km || name?.vi
      }
      if(typeof name === 'string') {
        return name
      }
      return name
    },
    avatarText(name) {
      if (!name) return "";
      return name.charAt(0).toUpperCase();
    },
  },
};
</script>

<style lang="scss" scoped>
.service-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(255, 159, 67, 0.12);
  cursor: pointer;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 2px solid #ff9f43;
    animation: pulse 2s ease-out infinite;
    opacity: 0;
  }

  svg {
    color: #ff9f43;
    stroke-width: 2.5px;
  }
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 0;
  }
  100% {
    transform: scale(0.95);
    opacity: 0;
  }
}

.pulsing-icon {
  animation: glow 2s ease-in-out infinite;
}

@keyframes glow {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}
</style>

